import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const AlumniRegistration = () => {
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [examBodies, setExamBodies] = useState([]);
  const [graduationDays, setGraduationDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [countdown, setCountdown] = useState(50);
  const [copiedField, setCopiedField] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    email: '',
    admissionNumber: '',
    mobileNumber: '',
    departmentId: '',
    courseId: '',
    examBodyId: '',
    grade: '',
    graduationDayId: ''
  });

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (showSuccessModal && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setShowSuccessModal(false);
      setCountdown(50);
      setCopiedField(null);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal, countdown]);

  const getGradeOptions = (examBodyId) => {
    const examBody = examBodies.find(eb => eb.id === parseInt(examBodyId));
    if (!examBody) return [];

    switch(examBody.code) {
      case 'TVET-CDACC':
        return [
          { value: 'Mastery', label: 'Mastery' },
          { value: 'Proficiency', label: 'Proficiency' },
          { value: 'Competent', label: 'Competent' }
        ];
      case 'KNEC':
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
      case 'NITA':
        return [
          { value: 'First Class', label: 'First Class' },
          { value: 'Pass', label: 'Pass' }
        ];
      default:
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
    }
  };

  const getCourseDisplayName = (course) => {
    let displayName = course.name;
    if (course.exam_body_code === 'TVET-CDACC' && course.level_number) {
      displayName = displayName + ' (Level ' + course.level_number + ')';
    } else if (course.level_name && course.level_name.trim() !== '') {
      displayName = displayName + ' (' + course.level_name + ')';
    }
    return displayName;
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.departmentId) {
      const filtered = courses.filter(course => course.department_id === parseInt(formData.departmentId));
      setFilteredCourses(filtered);
      setFormData(prev => ({ ...prev, courseId: '' }));
      setCourseSearchTerm('');
    } else {
      setFilteredCourses([]);
    }
  }, [formData.departmentId, courses]);

  useEffect(() => {
    if (courseSearchTerm && formData.departmentId) {
      const filtered = courses.filter(course =>
        course.department_id === parseInt(formData.departmentId) &&
        course.name.toLowerCase().includes(courseSearchTerm.toLowerCase())
      );
      setFilteredCourses(filtered);
      setShowCourseDropdown(true);
    } else if (formData.departmentId) {
      const filtered = courses.filter(course => course.department_id === parseInt(formData.departmentId));
      setFilteredCourses(filtered);
      setShowCourseDropdown(false);
    }
  }, [courseSearchTerm, courses, formData.departmentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deptsRes, coursesRes, examRes, graduationDaysRes] = await Promise.all([
        api.get('/departments'),
        api.get('/courses'),
        api.get('/exam-bodies'),
        api.get('/graduation-days/past')
      ]);

      setDepartments(deptsRes.data);
      setCourses(coursesRes.data);
      setExamBodies(examRes.data);
      setGraduationDays(graduationDaysRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdmissionNumberChange = (e) => {
    let value = e.target.value.toUpperCase();
    setFormData({ ...formData, admissionNumber: value });
  };

  const handleMobileNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.startsWith('0') && value.length <= 10) {
      value = value.substring(0, 10);
    } else if (value.startsWith('254') && value.length <= 12) {
      value = value.substring(0, 12);
    } else if (value.length <= 9) {
      value = value;
    }
    setFormData({ ...formData, mobileNumber: value });
  };

  const handleCourseSelect = (course) => {
    setFormData({ ...formData, courseId: course.id });
    setCourseSearchTerm(getCourseDisplayName(course));
    setShowCourseDropdown(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(field + ' copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.secondName || !formData.email ||
        !formData.admissionNumber || !formData.mobileNumber || !formData.departmentId ||
        !formData.courseId || !formData.examBodyId || !formData.grade || !formData.graduationDayId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const phoneRegex = /^(07\d{8}|2547\d{8})$/;
    if (!phoneRegex.test(formData.mobileNumber)) {
      toast.error('Please enter a valid Kenyan phone number');
      return;
    }

    const admissionRegex = /^[A-Z]{3}\/\d{3}\/[A-Z]\d{4}$/;
    if (!admissionRegex.test(formData.admissionNumber)) {
      toast.error('Invalid admission number format. Use format: DEE/001/S2021');
      return;
    }

    setSubmitting(true);
    try {
      const alumniData = {
        name: formData.firstName + ' ' + formData.secondName,
        email: formData.email,
        admin_number: formData.admissionNumber,
        telephone: formData.mobileNumber,
        department_id: parseInt(formData.departmentId),
        course_id: parseInt(formData.courseId),
        exam_body_id: parseInt(formData.examBodyId),
        grade: formData.grade,
        graduation_day_id: parseInt(formData.graduationDayId)
      };

      const response = await api.post('/alumni/register', alumniData);

      const selectedDept = departments.find(d => d.id === parseInt(formData.departmentId));
      const selectedCourse = courses.find(c => c.id === parseInt(formData.courseId));
      const selectedGradDay = graduationDays.find(g => g.id === parseInt(formData.graduationDayId));

      setRegistrationData({
        name: formData.firstName + ' ' + formData.secondName,
        email: formData.email,
        admin_number: formData.admissionNumber,
        telephone: formData.mobileNumber,
        membership_number: response.data.alumni_membership_number || 'Pending...',
        department: selectedDept?.name || 'N/A',
        course: selectedCourse ? getCourseDisplayName(selectedCourse) : 'N/A',
        grade: formData.grade,
        graduation_year: selectedGradDay?.year || 'N/A',
        username: formData.mobileNumber,
        password: formData.admissionNumber
      });

      setShowSuccessModal(true);
      setCountdown(50);

      // Reset form
      setFormData({
        firstName: '',
        secondName: '',
        email: '',
        admissionNumber: '',
        mobileNumber: '',
        departmentId: '',
        courseId: '',
        examBodyId: '',
        grade: '',
        graduationDayId: ''
      });
      setCourseSearchTerm('');
      setShowCourseDropdown(false);

    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentGradeOptions = getGradeOptions(formData.examBodyId);
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-KE', options);
  };

  return (
    <>
      <Header />
      <div style={{ backgroundColor: '#FEFBEC', minHeight: 'calc(100vh - 140px)' }}>
        {/* Hero Section */}
        <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg sm:text-xl">🎓</span>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">Alumni Registration</h1>
                <p className="text-xs sm:text-sm text-gray-600">Join our alumni community and stay connected with Katine TTI</p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto">
            <Card className="p-4 sm:p-6 md:p-8">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <Input label="First Name *" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} placeholder="Enter your first name" required />
                  <Input label="Second Name *" value={formData.secondName} onChange={(e) => setFormData({...formData, secondName: e.target.value})} placeholder="Enter your second name" required />
                  <Input label="Email Address *" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your.email@example.com" required />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admission Number *</label>
                    <Input type="text" value={formData.admissionNumber} onChange={handleAdmissionNumberChange} placeholder="DEE/001/S2021" className="uppercase" required />
                    <p className="text-xs text-gray-500 mt-1">Format: DEE/001/S2021 (auto-capitalized)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                    <Input type="tel" value={formData.mobileNumber} onChange={handleMobileNumberChange} placeholder="0712345678 or 254712345678" required />
                    <p className="text-xs text-gray-500 mt-1">Kenyan format (e.g., 0712345678)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" value={formData.departmentId} onChange={(e) => setFormData({...formData, departmentId: e.target.value, courseId: '', courseSearchTerm: ''})} required>
                      <option value="">-- Select Department --</option>
                      {departments.map(dept => (<option key={dept.id} value={dept.id}>{dept.name}</option>))}
                    </select>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder={formData.departmentId ? "Start typing to search course..." : "Select department first"} value={courseSearchTerm} onChange={(e) => setCourseSearchTerm(e.target.value)} onFocus={() => formData.departmentId && setShowCourseDropdown(true)} onBlur={() => setTimeout(() => setShowCourseDropdown(false), 200)} disabled={!formData.departmentId} />
                    {showCourseDropdown && filteredCourses.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {filteredCourses.map(course => (
                          <div key={course.id} onClick={() => handleCourseSelect(course)} className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0">
                            <div className="font-medium">{getCourseDisplayName(course)}</div>
                            {course.level_name && course.level_name.trim() !== '' && (
                              <div className="text-xs text-gray-500">{course.level_name}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!formData.departmentId && <p className="text-xs text-gray-500 mt-1">Please select a department first</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exam Body *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" value={formData.examBodyId} onChange={(e) => setFormData({...formData, examBodyId: e.target.value, grade: ''})} required>
                      <option value="">-- Select Exam Body --</option>
                      {examBodies.map(body => (<option key={body.id} value={body.id}>{body.name}</option>))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" value={formData.grade} onChange={(e) => setFormData({...formData, grade: e.target.value})} required disabled={!formData.examBodyId}>
                      <option value="">-- Select Grade --</option>
                      {currentGradeOptions.map(grade => (<option key={grade.value} value={grade.value}>{grade.label}</option>))}
                    </select>
                    {!formData.examBodyId && <p className="text-xs text-gray-500 mt-1">Please select an exam body first</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Ceremony *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" value={formData.graduationDayId} onChange={(e) => setFormData({...formData, graduationDayId: e.target.value})} required disabled={graduationDays.length === 0}>
                      <option value="">-- Select Graduation Ceremony --</option>
                      {graduationDays.map(day => (<option key={day.id} value={day.id}>{formatDate(day.ceremony_date)} - {day.year}{day.venue && ` @ ${day.venue}`}</option>))}
                    </select>
                    {graduationDays.length === 0 && <p className="text-xs text-yellow-600 mt-1">No past graduation ceremonies available.</p>}
                  </div>
                </div>

                <div className="mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-blue-800"><strong>ℹ️ Login Information:</strong><br />Your username will be your mobile number and your password will be your admission number.<br />You will be able to log in after admin approval.</p>
                </div>

                <div className="mt-6">
                  <Button type="submit" className="w-full py-3 sm:py-4 text-base sm:text-lg" disabled={submitting || graduationDays.length === 0}>
                    {submitting ? 'Registering...' : 'Register as Alumnus'}
                  </Button>
                </div>
              </form>
            </Card>

            <div className="text-center mt-6">
              <p className="text-sm sm:text-base text-gray-600">
                Already registered? <Link to="/alumni/login" className="text-primary hover:underline">Login to Alumni Portal</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Success Modal */}
      {showSuccessModal && registrationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎉</span>
                <h2 className="text-xl font-bold">Registration Successful!</h2>
              </div>
              <button onClick={() => setShowSuccessModal(false)} className="text-white hover:text-gray-200 text-2xl">&times;</button>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-4xl">✓</span>
                </div>
                <p className="text-gray-600">Your alumni registration has been completed successfully.</p>
                <p className="text-sm text-gray-500 mt-1">A confirmation email has been sent to your email address.</p>
                <p className="text-sm text-gray-500">This modal will auto-close in <strong>{countdown}</strong> seconds</p>
              </div>
              
              {/* Membership Details */}
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-primary">🎓 Alumni Membership Details</h3>
                  <button 
                    onClick={() => copyToClipboard(registrationData.membership_number, 'Membership Number')}
                    className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark"
                  >
                    {copiedField === 'Membership Number' ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-2xl font-bold text-primary text-center">{registrationData.membership_number}</p>
              </div>
              
              {/* Personal Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Full Name</label>
                  <p className="font-medium">{registrationData.name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Admission Number</label>
                  <p className="font-mono text-sm">{registrationData.admin_number}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Email Address</label>
                  <p className="text-sm break-all">{registrationData.email}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Phone Number</label>
                  <p>{registrationData.telephone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Department</label>
                  <p className="text-sm">{registrationData.department}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Course</label>
                  <p className="text-sm">{registrationData.course}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Grade</label>
                  <p>{registrationData.grade}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500">Graduation Year</label>
                  <p>{registrationData.graduation_year}</p>
                </div>
              </div>
              
              {/* Login Credentials */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-yellow-800 mb-2">🔐 Login Credentials</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Username:</span>
                    <code className="text-sm bg-white px-2 py-1 rounded">{registrationData.username}</code>
                    <button 
                      onClick={() => copyToClipboard(registrationData.username, 'Username')}
                      className="text-xs text-primary hover:underline"
                    >
                      {copiedField === 'Username' ? '✓' : 'Copy'}
                    </button>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Password:</span>
                    <code className="text-sm bg-white px-2 py-1 rounded">{registrationData.password}</code>
                    <button 
                      onClick={() => copyToClipboard(registrationData.password, 'Password')}
                      className="text-xs text-primary hover:underline"
                    >
                      {copiedField === 'Password' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowSuccessModal(false); window.location.href = '/alumni/login'; }}
                  className="flex-1 bg-primary text-white py-2 rounded-lg hover:bg-primary-dark"
                >
                  Proceed to Login
                </button>
                <button 
                  onClick={() => setShowSuccessModal(false)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlumniRegistration;
